import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useBlackoutDatesByRoomTypes } from '~/hooks/apis/stopsell/useBlackoutDatesByRoomTypes';
import { useExtractStopsellDates } from '~/hooks/apis/stopsell/useExtractStopsellDates';

import { CatalogRoom, StopsellExtractionResult } from '~/types/stopsell';

import StopsellDocumentUploadZone from './StopsellDocumentUploadZone';
import StopsellReviewModal from './StopsellReviewModal';

interface Props {
  propertyId: string;
  roomTypes: Array<App.RoomType>;
}

export default function StopsellManagementPane({ propertyId, roomTypes }: Props) {
  const { mutate, isPending, error } = useExtractStopsellDates();
  const [extractionResult, setExtractionResult] = useState<StopsellExtractionResult | null>(null);
  const [pendingExtraction, setPendingExtraction] = useState<StopsellExtractionResult | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [reviewOpen, setReviewOpen] = useState(false);

  const {
    isLoading: isBlackoutLoading,
    isError: isBlackoutError,
    byRoomType: blackoutsByRoomType,
  } = useBlackoutDatesByRoomTypes(propertyId, roomTypes);

  const catalogRooms = useMemo<Array<CatalogRoom>>(
    () =>
      roomTypes.map((r) => ({
        id: r.id,
        name: r.name,
        blackoutDates: blackoutsByRoomType[r.id] ?? [],
      })),
    [roomTypes, blackoutsByRoomType],
  );

  // onSuccess captures isBlackoutLoading via stale closure, so when extraction finishes before
  // blackout data is ready we store the result in pendingExtraction and flush it here once
  // isBlackoutLoading settles, ensuring conflict detection always runs against complete data.
  useEffect(() => {
    if (pendingExtraction && !isBlackoutLoading) {
      setExtractionResult(pendingExtraction);
      setReviewOpen(true);
      setPendingExtraction(null);
    }
  }, [pendingExtraction, isBlackoutLoading]);

  const handleSelectFile = useCallback(
    (file: File) => {
      setFileName(file.name);
      mutate(
        { file, catalogRooms },
        {
          onSuccess: (data: StopsellExtractionResult) => {
            if (isBlackoutLoading) {
              setPendingExtraction(data);
            } else {
              setExtractionResult(data);
              setReviewOpen(true);
            }
          },
        },
      );
    },
    [mutate, catalogRooms, isBlackoutLoading],
  );

  // TODO: XTRNT-576 — submit selected stopsell dates to the API
  const handleApply = useCallback((_selected: StopsellExtractionResult) => {
    setReviewOpen(false);
  }, []);

  return (
    <>
      <PageSubheader title="Stopsell Management" />
      <StopsellDocumentUploadZone onSelectFile={handleSelectFile} isPending={isPending} error={error?.message} />

      {extractionResult && (
        <StopsellReviewModal
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          onApply={handleApply}
          extraction={extractionResult}
          fileName={fileName}
          catalogRooms={catalogRooms}
          isBlackoutError={isBlackoutError}
        />
      )}
    </>
  );
}
